import { FABRIC_HEADERS } from '@/utilities/constants/metadata';
import config from '@/config';
import { ErrorType } from '@/utilities/errors/types';
import { APIServiceType } from '../types';
import { CustomError } from '@/utilities/errors/CustomError';
import {
  buildFabricAuthHeaders,
  createApiAxios,
  getFabricAuthTokens,
} from '@/utilities/axios/api-axios';
import { getLocalAccountSession } from '@/utilities/getters/sessionContextGetters.ts';
import { useStore } from '@/store/useStore';
import { logAndThrowApiError } from '@/api/helpers/log-and-throw-api-error';

export interface AddTrendingGamePayload {
  gameId: string;
}

/**
 * Adds a game to trending via API
 */
export const addTrendingGameApi = async ({
  gameId,
}: AddTrendingGamePayload): Promise<void> => {
  const endpoint = config?.fabric?.endpoints?.trendingGames?.addGame;
  const { baseURL, region, brand, platform, product, channel } =
    useStore.getState().runtime;
  const accId = getLocalAccountSession()?.userId;

  if (!endpoint || !product || !platform || !region || !gameId) {
    throw new CustomError('[GW][TRENDING_GAMES] Missing required parameters', {
      type: ErrorType.API,
      reason: 'Missing endpoint, product, platform, region, or gameId',
      service: APIServiceType.TRENDING_GAMES,
    });
  }

  const apiUrl = `${endpoint}`;

  try {
    const tokens = await getFabricAuthTokens('TRENDING_GAMES');

    const instance = createApiAxios();
    await instance.get(apiUrl, {
      baseURL,
      params: {
        accId,
        gameId,
        channel,
        product,
      },
      headers: {
        ...buildFabricAuthHeaders(tokens),
        [FABRIC_HEADERS.X_BRAND]: brand,
        [FABRIC_HEADERS.X_REGION_CODE]: region,
      },
    });
  } catch (error) {
    logAndThrowApiError({
      error,
      logName:
        error instanceof Error && error.message.includes('[GW]')
          ? error.message
          : '[GW][TRENDING_GAMES] Failed to add trending game',
      description: 'Failed to add trending game',
      service: APIServiceType.TRENDING_GAMES,
      customErrorMessage: '[GW][TRENDING_GAMES] Error adding trending game',
      context: {
        gameId,
      },
    });
  }
};

export const addTrendingGame = async (
  payload: AddTrendingGamePayload,
): Promise<void> => addTrendingGameApi(payload);
